import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { Seo } from "@/components/Seo";

export default function CartPage() {
  const { t, lang } = useLang();
  const s = t.shop;
  const { items, setQty, remove, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-brand-soft/30">
      <Seo title={t.seo.cart_title} path="/cart" noindex />
      <Navbar />
      <main className="pt-28 pb-24 px-5 sm:px-8 max-w-3xl mx-auto" data-testid="cart-page">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-brand-ink flex items-center gap-3">
          <ShoppingBag className="h-8 w-8 text-brand" /> {s.cart_title}
        </h1>

        {items.length === 0 ? (
          <div className="mt-12 text-center bg-white rounded-[2rem] border border-brand/10 p-12" data-testid="cart-empty">
            <p className="text-brand-ink/50">{s.cart_empty}</p>
            <Button onClick={() => navigate("/shop")} className="mt-6 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="cart-continue">
              <ArrowLeft className="h-4 w-4 mr-2" /> {s.continue_shopping}
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-8 space-y-4">
              {items.map((x, i) => (
                <motion.div
                  key={`${x.product_id}-${x.variant_id}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-4 bg-white rounded-3xl border border-brand/10 p-4"
                  data-testid={`cart-item-${x.product_id}`}
                >
                  <div className="h-20 w-20 rounded-2xl bg-stone-alt/50 overflow-hidden shrink-0">
                    {x.image && <img src={`${API}/${x.image}`} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading text-lg font-bold text-brand-ink truncate">{(lang === "en" && x.name_en) ? x.name_en : x.name_gr}</h3>
                    {x.variant_label && <p className="text-xs text-brand-ink/50">{x.variant_label}</p>}
                    <p className="text-sm font-bold text-brand mt-0.5">€{Number(x.price).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center rounded-full border border-brand/20">
                    <button onClick={() => setQty(x.product_id, x.variant_id, x.qty - 1)} className="h-9 w-9 grid place-items-center text-brand-ink" data-testid={`cart-minus-${x.product_id}`}>
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-7 text-center text-sm font-bold text-brand-ink" data-testid={`cart-qty-${x.product_id}`}>{x.qty}</span>
                    <button onClick={() => setQty(x.product_id, x.variant_id, x.qty + 1)} disabled={x.qty >= (x.max ?? 20)} title={x.max && x.qty >= x.max ? s.max_stock : undefined} className="h-9 w-9 grid place-items-center text-brand-ink disabled:opacity-30" data-testid={`cart-plus-${x.product_id}`}>
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="w-20 text-right font-bold text-brand-ink">€{(x.price * x.qty).toFixed(2)}</p>
                  <button onClick={() => remove(x.product_id, x.variant_id)} className="grid place-items-center h-9 w-9 rounded-full text-rose-500 hover:bg-rose-50" data-testid={`cart-remove-${x.product_id}`} aria-label={s.remove}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 bg-white rounded-3xl border border-brand/10 p-6">
              <div className="flex items-center justify-between">
                <span className="text-brand-muted font-semibold">{s.subtotal}</span>
                <span className="font-heading text-2xl font-bold text-brand-ink" data-testid="cart-subtotal">€{subtotal.toFixed(2)}</span>
              </div>
              <p className="mt-2 text-xs text-brand-ink/50">{s.pickup_note}</p>
              <Button onClick={() => navigate("/shop/checkout")} className="mt-5 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 text-base" data-testid="go-checkout-btn">
                {s.checkout_btn} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
